/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomDialogBoxV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
} from "@mui/material";
import ContiniousConfirmButton from "../customDialogBox/actionButton/ContiniousConfirmButton";
import SpacedConfirmButton from "../customDialogBox/actionButton/SpacedConfirmButton";
import ThreeActionButton from "../customDialogBox/actionButton/ThreeActionButton";
import CloseIcon from "@mui/icons-material/Close";
import { componentStyle } from "../../../../config/appTheme";

function CustomDialogBoxV2({
  open,
  onClose,
  title,
  children,
  successButtonName,
  rejectButtonName,
  onClickSuccess,
  onClickReject,
  otherButtonName,
  onOtherButtonClick,
  actionButtonStyle,
  onDisabled = false,
  dialogActions = null,
  rejectDisabled = false,
  rejectButtonType,
  paperSx = {},
  contentSx = {},
  dialogActionsSx = {},
  dialogContentProps = {},
  disabled,
  typeForThree,
  otherButtonPlacement,
  dialogTitleSx = {},
  dialogRadius = "1rem",
  shouldBeResponsive = false,
  headerSlot = null,
  footerSlot = null,
  hideDefaultActions = false,
  requiresDefaultPaddingForContent = true,
  maxWidth,
  fullScreen = false,
  sx = {},
}) {
  console.log("CustomDialogBoxV2 Rendered with title:", title);
  const actionButton = () => {
    switch (actionButtonStyle) {
      case "continuous":
        return (
          <ContiniousConfirmButton
            successButtonName={successButtonName}
            rejectButtonName={rejectButtonName}
            onClickSuccess={onClickSuccess}
            onClickReject={onClickReject}
            onDisabled={onDisabled}
          />
        );
      case "spaced":
        return (
          <SpacedConfirmButton
            successButtonName={successButtonName}
            rejectButtonName={rejectButtonName}
            onClickSuccess={onClickSuccess}
            onClickReject={onClickReject}
          />
        );
      case "three":
        return (
          <ThreeActionButton
            onClickSuccess={onClickSuccess}
            onClickReject={onClickReject}
            successButtonName={successButtonName}
            rejectButtonName={rejectButtonName}
            otherButtonName={otherButtonName}
            onOtherButtonClick={onOtherButtonClick}
            disabled={disabled}
            onDisabled={onDisabled}
            rejectDisabled={rejectDisabled}
            rejectButtonType={rejectButtonType}
            typeForThree={typeForThree}
            otherButtonPlacement={otherButtonPlacement}
          />
        );

      default:
        return dialogActions;
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={shouldBeResponsive}
      fullScreen={fullScreen}
      sx={sx}
      PaperProps={{
        sx: {
          borderRadius: dialogRadius,
          width: maxWidth ? "" : "fit-content",
          ...(maxWidth ? {} : { minWidth: "fit-content" }),
          ...paperSx,
        },
      }}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle
        id="responsive-dialog-title"
        sx={{
          position: "relative",
          p: headerSlot ? 0 : 2,
          borderBottom: "none",
          ...dialogTitleSx,
        }}
      >
        {headerSlot ?? (
          <>
            <Box sx={{ ...componentStyle.font.cardHeadder }}>{title}</Box>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </>
        )}
      </DialogTitle>

      <DialogContent
        {...dialogContentProps}
        sx={{
          ...(requiresDefaultPaddingForContent
            ? {
                maxHeight: "70vh",
                overflowY: "auto",
                padding: "20px 24px",
                scrollbarWidth: "thin",
                scrollbarColor: "#DDDDDD transparent",

                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#DDDDDD",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-button": {
                  display: "none",
                  height: 0,
                  width: 0,
                  background: "transparent",
                },
              }
            : {
                maxHeight: "70vh",
                overflowY: "auto",
                padding: "0px !important",
                scrollbarWidth: "thin",
                scrollbarColor: "#DDDDDD transparent",

                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#DDDDDD",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-button": {
                  display: "none",
                  height: 0,
                  width: 0,
                  background: "transparent",
                },
              }),

          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {!hideDefaultActions && (
        <DialogActions sx={{ pr: 3, pl: 3, ...dialogActionsSx }}>
          {actionButton()}
        </DialogActions>
      )}

      {footerSlot}
    </Dialog>
  );
}

CustomDialogBoxV2.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.node,
  children: PropTypes.node,
  successButtonName: PropTypes.string,
  rejectButtonName: PropTypes.string,
  onClickSuccess: PropTypes.func,
  onClickReject: PropTypes.func,
  otherButtonName: PropTypes.string,
  onOtherButtonClick: PropTypes.func,
  actionButtonStyle: PropTypes.string,
  onDisabled: PropTypes.bool,
  dialogActions: PropTypes.node,
  rejectDisabled: PropTypes.bool,
  rejectButtonType: PropTypes.string,
  paperSx: PropTypes.object,
  contentSx: PropTypes.object,
  dialogActionsSx: PropTypes.object,
  dialogContentProps: PropTypes.object,
  disabled: PropTypes.bool,
  typeForThree: PropTypes.string,
  otherButtonPlacement: PropTypes.string,
  dialogTitleSx: PropTypes.object,
  dialogRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shouldBeResponsive: PropTypes.bool,
  headerSlot: PropTypes.node,
  footerSlot: PropTypes.node,
  sx: PropTypes.object,
  hideDefaultActions: PropTypes.bool,
  requiresDefaultPaddingForContent: PropTypes.bool,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  fullScreen: PropTypes.bool,
};

export default CustomDialogBoxV2;
